/*
 * The decisions behind CompanyLookup, kept out of the component so they can
 * be tested without rendering it.
 *
 * A register is a CompanyInfo provider: DK (CVR), FR (Sirene), EU (VIES).
 * The server lists them (GET company-info/registers) with `nameSearch`; EU is
 * false — VIES finds a company by its full VAT number only.
 */

export const MIN_LENGTH = 3

const compact = (q) => String(q ?? '').toUpperCase().replace(/[\s.-]/g, '')

/** Which register a lookup searches: the one the user chose, else the form's country, else the org's. */
export const registerFor = (chosen, formCountry, orgCountry) => chosen || formCountry || orgCountry || null

/** A prefixed VAT number: two letters, then a number with digits ("Muster GmbH" is not one). */
export const isVatNumber = (q) => {
    const c = compact(q)
    return /^[A-Z]{2}[A-Z0-9]{2,12}$/.test(c) && /\d/.test(c.slice(2))
}

/** A CVR number (DK), a SIREN, SIRET or VAT number made of one (FR), or any prefixed VAT number. */
export const isCompanyId = (q) => /^(DK)?\d{8}$|^(FR[0-9A-Z]{2})?\d{9}(\d{5})?$/.test(compact(q)) || isVatNumber(q)

/**
 * What the typed text does in this register:
 *   'search'   look it up
 *   'vatOnly'  a register that finds by VAT number only, and this is not one: say so
 *   'none'     too short to mean anything yet
 *
 * @param {{ nameSearch?: boolean }|undefined} info  the register's entry from company-info/registers
 */
export const searchMode = (info, query) => {
    const q = String(query ?? '').trim()
    if (!q) return 'none'
    if (info?.nameSearch === false) {
        if (isVatNumber(q)) return 'search'
        return q.length >= MIN_LENGTH ? 'vatOnly' : 'none'
    }
    return q.length >= MIN_LENGTH || isCompanyId(q) ? 'search' : 'none'
}

const readPath = (obj, path) => path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj)

/**
 * The form changes a picked company makes: every field in `valueMap`, and the
 * company's country — the register it came from says where it is, whatever
 * the form said before. The field being typed in keeps what was typed when
 * the register has no value for it (VIES withholds names for some states).
 *
 * @returns {{ target: { name: string, value: any } }[]}
 */
export const pickChanges = (company, valueMap, typedField) => {
    const changes = []
    for (const [field, path] of Object.entries(valueMap)) {
        const value = readPath(company, path)
        if (field === typedField && !value) continue
        changes.push({ target: { name: field, value: value ?? '' } })
    }
    if (company.country) {
        changes.push({ target: { name: 'country', value: company.country } })
        if (company.countryName) changes.push({ target: { name: 'countryName', value: company.countryName } })
    }
    return changes
}

/*
 * Texts. Every register has its own block of texts, in its own words — CVR
 * finds an exact name, Sirene and Brønnøysund a name's beginning, VIES a VAT
 * number:
 *
 *   all.companyLookup.<REGISTER>.label        its name in the register menu
 *   all.companyLookup.<REGISTER>.companyId    what its id is called (CVR, SIREN…)
 *   all.companyLookup.<REGISTER>.placeholder  the field's placeholder while it is chosen
 *   all.companyLookup.<REGISTER>.searching    while a lookup runs
 *   all.companyLookup.<REGISTER>.noMatch      when it finds nothing
 *   all.companyLookup.<REGISTER>.hint         when the text typed is not something it can find (EU)
 *
 * A text a register lacks falls back to the neutral all.companyLookup.<text>,
 * so a register added later reads sensibly before its block is written; a
 * missing placeholder leaves the field's own. Adding a register's texts is one
 * block per language.
 */
const TEXTS = 'all.companyLookup'

/** The register's own text, or null when it has none. `t` answers the key itself for a missing text. */
export const providerText = (t, register, text) => {
    if (!register) return null
    const key = `${TEXTS}.${register}.${text}`
    const value = t(key)
    return value === key ? null : value
}

/** The register's own text, else the neutral one. */
export const registerText = (t, text, register) => providerText(t, register, text) ?? t(`${TEXTS}.${text}`)

/** How a register is named in its menu: its own label, else the server's country name. */
export const registerLabel = (register, t) => providerText(t, register.id, 'label') || register.countryName || register.id
